import {div, filler} from '@xh/hoist/cmp/layout';
import {hoistCmp, HoistProps, uses} from '@xh/hoist/core';
import {button} from '@xh/hoist/desktop/cmp/button';
import {select, textInput} from '@xh/hoist/desktop/cmp/input';
import {panel} from '@xh/hoist/desktop/cmp/panel';
import {toolbar} from '@xh/hoist/desktop/cmp/toolbar';
import {Icon} from '@xh/hoist/icon';
import {dialog} from '@xh/hoist/kit/blueprint';
import {configPanelItem, configPanelSwitch, sectionAddButton, stackedPanel} from '../../../cmp/gridSpec/configurator/Common';
import {ResearchDataFilter} from '../../../data/ResearchTypes';
import {FILTER_OP_OPTIONS, isValueless, ResearchDataDialogModel} from './ResearchDataDialogModel';
import '../ResearchTab.scss';

/** Picks what a research widget shows: the dataset, and how its rows are narrowed. */
export const researchDataDialog = hoistCmp.factory<ResearchDataDialogModel>({
    model: uses(ResearchDataDialogModel),
    render({model}) {
        if (!model.isOpen) return null;

        const {draft, dataset, filters, kind} = model;

        return dialog({
            title: 'Widget Data',
            icon: Icon.database(),
            isOpen: true,
            style: {width: 460},
            canOutsideClickClose: false,
            onClose: () => model.close(),
            item: panel({
                className: 'research-data-dialog',
                scrollable: true,
                contentBoxProps: {maxHeight: '70vh'},
                items: [
                    stackedPanel({
                        title: 'Dataset',
                        items: [
                            configPanelItem({
                                label: 'Dataset',
                                item: select({
                                    value: draft.dataset,
                                    onChange: v => model.setDataset(v),
                                    options: model.datasetOptions,
                                    placeholder: 'Select a dataset...',
                                    width: 280
                                })
                            }),
                            div({
                                className: 'research-data-dialog__description',
                                omit: !dataset,
                                items: [div(dataset?.description), div({className: 'research-data-dialog__grain', item: dataset?.grain})]
                            })
                        ]
                    }),
                    stackedPanel({
                        title: 'Rows',
                        omit: !dataset,
                        items: [
                            configPanelItem({
                                omit: !dataset?.supportsLatest,
                                item: configPanelSwitch({
                                    label: 'Newest date only',
                                    value: !!draft.latest,
                                    onChange: latest => model.update({latest})
                                })
                            }),
                            configPanelItem({
                                omit: kind !== 'grid',
                                item: configPanelSwitch({
                                    label: 'Newest row, one line per field',
                                    value: !!draft.transpose,
                                    onChange: transpose => model.update({transpose})
                                })
                            }),
                            configPanelItem({
                                label: 'Only rows where...',
                                item: sectionAddButton(() => model.addFilter(), 'Add filter')
                            }),
                            ...filters.map((filter, idx) => filterRow({key: `filter-${idx}`, model, idx}))
                        ]
                    })
                ],
                bbar: toolbar(
                    filler(),
                    button({text: 'Cancel', onClick: () => model.close()}),
                    button({text: 'Apply', intent: 'success', disabled: !model.canSave, onClick: () => model.save()})
                )
            })
        });
    }
});

interface ValueInputProps extends HoistProps {
    filter: ResearchDataFilter;
    /** The field's values, when it is an enum. */
    valueOptions: string[];
    /** True when the filter compares with a list of values. */
    isList: boolean;
    onChange: (value: any) => void;
}

/** The value a filter compares with: picked for an enum, typed otherwise - a list is typed with commas between its values. */
const valueInput = hoistCmp.factory<ValueInputProps>({
    model: false,
    render({filter, valueOptions, isList, onChange}) {
        if (valueOptions) {
            return select({value: filter.value, onChange, options: valueOptions, enableMulti: isList, enableFilter: false, placeholder: 'Value', width: 140});
        }
        if (!isList) return textInput({value: filter.value, onChange, commitOnChange: true, placeholder: 'Value', width: 140});

        return textInput({
            value: (filter.value ?? []).join(', '),
            onChange: (text: string) => onChange(splitList(text)),
            placeholder: 'Values, comma separated',
            width: 140
        });
    }
});

function splitList(text: string): string[] {
    return (text ?? '')
        .split(',')
        .map(v => v.trim())
        .filter(Boolean);
}

const filterRow = hoistCmp.factory<ResearchDataDialogModel>({
    render({model, idx}) {
        const filter = model.filters[idx],
            valueOptions = model.valueOptions(filter.field),
            isList = filter.op === 'in';

        return configPanelItem({
            omitFiller: true,
            items: [
                select({
                    value: filter.field,
                    onChange: field => model.updateFilter(idx, {field}),
                    options: model.filterFieldOptions,
                    placeholder: 'Field',
                    flex: 1,
                    width: null
                }),
                select({
                    className: 'research-data-dialog__op',
                    value: filter.op,
                    onChange: op => model.updateFilter(idx, {op}),
                    options: FILTER_OP_OPTIONS,
                    enableFilter: false,
                    width: 95
                }),
                valueInput({omit: isValueless(filter.op), filter, valueOptions, isList, onChange: value => model.updateFilter(idx, {value})}),
                button({icon: Icon.x(), intent: 'danger', minimal: true, onClick: () => model.removeFilter(idx)})
            ]
        });
    }
});
